import { useState } from 'react'
import { Plus, Trash2, Edit2, Check, X, Image as ImageIcon, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { productsService } from '@/services/productsService'
import ConfirmDialog from './ConfirmDialog'

export default function ProductVariantsManager({ variants = [], onChange, productId, isCreate }) {
  const [editingIndex, setEditingIndex] = useState(-1)
  const [formData, setFormData] = useState({})
  const [deleteDialog, setDeleteDialog] = useState({ open: false, index: -1, variant: null })
  
  // Generator State
  const [showGenerator, setShowGenerator] = useState(false)
  const [genData, setGenData] = useState({
    opt1Name: 'الطول', opt1Values: '',
    opt2Name: 'العرض', opt2Values: '',
    basePrice: '', baseStock: '10'
  })
  const [isGenerating, setIsGenerating] = useState(false)

  const handleEdit = (index, variant) => {
    setEditingIndex(index)
    setFormData({
      name: variant.name || '',
      options: variant.options ? Object.entries(variant.options).map(([k, v]) => `${k}:${v}`).join(', ') : '',
      price: variant.price || '',
      sku: variant.sku || '',
      stockQuantity: variant.stockQuantity || '',
    })
  }

  const handleSave = async (index) => {
    if (!formData.name || !formData.price || !formData.stockQuantity) {
      toast.error('يرجى ملء الاسم، السعر، والمخزون للمتغير')
      return
    }

    const optionsObj = {}
    if (formData.options) {
      formData.options.split(',').forEach(pair => {
        const [k, v] = pair.split(':')
        if (k && v) optionsObj[k.trim()] = v.trim()
      })
    }

    const newVariantData = {
      name: formData.name,
      options: optionsObj,
      price: parseFloat(formData.price),
      sku: formData.sku,
      stockQuantity: parseInt(formData.stockQuantity, 10),
      isActive: true,
    }

    if (isCreate) {
      const updated = [...variants]
      if (index === -1) {
        updated.push({ id: 'temp_' + Date.now().toString(), ...newVariantData })
      } else {
        updated[index] = { ...updated[index], ...newVariantData }
      }
      onChange(updated)
      setEditingIndex(-1)
      setFormData({})
    } else {
      try {
        if (index === -1) {
          const created = await productsService.addVariant(productId, newVariantData)
          onChange([...variants, created])
          toast.success('تم إضافة المتغير')
        } else {
          const v = variants[index]
          if (v.id && !v.id.toString().startsWith('temp')) {
            const updatedV = await productsService.updateVariant(productId, v.id, newVariantData)
            const updated = [...variants]
            updated[index] = updatedV
            onChange(updated)
            toast.success('تم تعديل المتغير')
          } else {
             const updated = [...variants]
             updated[index] = { ...updated[index], ...newVariantData }
             onChange(updated)
          }
        }
        setEditingIndex(-1)
        setFormData({})
      } catch (e) {
        toast.error('فشل حفظ المتغير')
      }
    }
  }

  const handleDelete = async () => {
    const { index, variant } = deleteDialog
    if (isCreate || (variant.id && variant.id.toString().startsWith('temp'))) {
      const updated = variants.filter((_, i) => i !== index)
      onChange(updated)
    } else {
      try {
        await productsService.deleteVariant(productId, variant.id)
        const updated = variants.filter((_, i) => i !== index)
        onChange(updated)
        toast.success('تم حذف المتغير')
      } catch (e) {
        toast.error('فشل حذف المتغير')
      }
    }
    setDeleteDialog({ open: false, index: -1, variant: null })
  }

  const handleGenerate = async () => {
    const vals1 = genData.opt1Values.split(',').map(v => v.trim()).filter(Boolean)
    const vals2 = genData.opt2Values.split(',').map(v => v.trim()).filter(Boolean)
    if (!vals1.length) return toast.error('أدخل قيم الخيار الأول على الأقل')
    
    setIsGenerating(true)
    const newVariants = []
    
    const createVariantObj = (v1, v2) => {
      let name = `مقاس ${v1}`
      let options = { [genData.opt1Name]: v1 }
      if (v2) {
        name += ` x ${v2}`
        options[genData.opt2Name] = v2
      }
      return {
        id: 'temp_' + Date.now() + Math.random(),
        name,
        options,
        price: parseFloat(genData.basePrice || 0),
        stockQuantity: parseInt(genData.baseStock || 0, 10),
        sku: '',
        isActive: true
      }
    }

    if (vals2.length) {
      vals1.forEach(v1 => {
        vals2.forEach(v2 => {
          newVariants.push(createVariantObj(v1, v2))
        })
      })
    } else {
      vals1.forEach(v1 => {
        newVariants.push(createVariantObj(v1))
      })
    }

    if (isCreate) {
      onChange([...variants, ...newVariants])
      setIsGenerating(false)
      setShowGenerator(false)
      toast.success(`تم توليد ${newVariants.length} متغيرات`)
    } else {
      const loadingToast = toast.loading('جار توليد المتغيرات ورفعها...')
      try {
        const createdVariants = []
        for (const nv of newVariants) {
           const created = await productsService.addVariant(productId, nv)
           createdVariants.push(created)
        }
        onChange([...variants, ...createdVariants])
        toast.success(`تم توليد ورفع ${newVariants.length} متغيرات بنجاح`, { id: loadingToast })
        setShowGenerator(false)
      } catch (e) {
        toast.error('حدث خطأ أثناء الرفع', { id: loadingToast })
      } finally {
        setIsGenerating(false)
      }
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {/* Matrix Generator Section */}
      {showGenerator && (
        <div style={{ background: 'rgba(255,109,0,0.05)', border: '1px dashed #FF6D00', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}>توليد المتغيرات تلقائياً (Matrix)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 10 }}>
            <input className="hg-input" placeholder="اسم الخيار 1 (مثال: الطول)" value={genData.opt1Name} onChange={e => setGenData({...genData, opt1Name: e.target.value})} />
            <input className="hg-input" placeholder="القيم (مثال: 190, 200, 210) مفصولة بفاصلة" value={genData.opt1Values} onChange={e => setGenData({...genData, opt1Values: e.target.value})} dir="ltr" />
            
            <input className="hg-input" placeholder="اسم الخيار 2 (مثال: العرض)" value={genData.opt2Name} onChange={e => setGenData({...genData, opt2Name: e.target.value})} />
            <input className="hg-input" placeholder="القيم (مثال: 100, 120) مفصولة بفاصلة" value={genData.opt2Values} onChange={e => setGenData({...genData, opt2Values: e.target.value})} dir="ltr" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input className="hg-input" type="number" placeholder="السعر الافتراضي لكل المتغيرات" value={genData.basePrice} onChange={e => setGenData({...genData, basePrice: e.target.value})} dir="ltr" />
            <input className="hg-input" type="number" placeholder="المخزون الافتراضي" value={genData.baseStock} onChange={e => setGenData({...genData, baseStock: e.target.value})} dir="ltr" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleGenerate} disabled={isGenerating} style={{ flex: 1, padding: 10, background: '#FF6D00', color: '#fff', borderRadius: 8, border: 'none', cursor: isGenerating ? 'wait' : 'pointer', fontFamily: 'Cairo' }}>
              {isGenerating ? 'جارٍ التوليد...' : 'توليد الآن'}
            </button>
            <button onClick={() => setShowGenerator(false)} style={{ padding: '10px 20px', background: '#F3F3F3', color: '#454652', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'Cairo' }}>إلغاء</button>
          </div>
        </div>
      )}

      {variants.map((v, i) => (
        <div key={v.id || i} style={{ background: '#FAFAFA', border: '1px solid #EBEBEB', borderRadius: 12, padding: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
          {editingIndex === i ? (
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input className="hg-input" placeholder="اسم المتغير *" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              <input className="hg-input" placeholder="الخيارات (مثال: color:Red, size:XL)" value={formData.options} onChange={e => setFormData({ ...formData, options: e.target.value })} dir="ltr" />
              <input className="hg-input" type="number" placeholder="السعر *" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} dir="ltr" />
              <input className="hg-input" type="number" placeholder="المخزون *" value={formData.stockQuantity} onChange={e => setFormData({ ...formData, stockQuantity: e.target.value })} dir="ltr" />
              <input className="hg-input" placeholder="SKU" value={formData.sku} onChange={e => setFormData({ ...formData, sku: e.target.value })} dir="ltr" style={{ gridColumn: '1 / -1' }}/>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => handleSave(i)} style={{ flex: 1, padding: 8, background: '#1B6D24', color: '#fff', borderRadius: 8, border: 'none', cursor: 'pointer' }}>حفظ</button>
                <button type="button" onClick={() => setEditingIndex(-1)} style={{ flex: 1, padding: 8, background: '#F3F3F3', color: '#454652', borderRadius: 8, border: 'none', cursor: 'pointer' }}>إلغاء</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#767683' }}>
                {v.imageUrl ? <img src={v.imageUrl} alt={v.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} /> : <ImageIcon size={20} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>{v.name}</div>
                <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                  سعر: {v.price} ج.م | مخزون: {v.stockQuantity} {v.sku && `| SKU: ${v.sku}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => handleEdit(i, v)} style={{ padding: 6, borderRadius: 8, border: 'none', background: 'rgba(0,6,102,0.1)', color: '#000666', cursor: 'pointer' }}><Edit2 size={14} /></button>
                <button type="button" onClick={() => setDeleteDialog({ open: true, index: i, variant: v })} style={{ padding: 6, borderRadius: 8, border: 'none', background: 'rgba(186,26,26,0.1)', color: '#BA1A1A', cursor: 'pointer' }}><Trash2 size={14} /></button>
              </div>
            </>
          )}
        </div>
      ))}

      {editingIndex === -1 && !showGenerator && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => handleEdit(-1, {})} style={{ flex: 1, padding: '10px', borderRadius: 12, border: '1px dashed #000666', background: 'rgba(0,6,102,0.03)', color: '#000666', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
            <Plus size={16} /> إضافة متغير يدوي
          </button>
          <button type="button" onClick={() => setShowGenerator(true)} style={{ flex: 1, padding: '10px', borderRadius: 12, border: '1px dashed #FF6D00', background: 'rgba(255,109,0,0.03)', color: '#FF6D00', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
            <Zap size={16} /> توليد المتغيرات (Matrix)
          </button>
        </div>
      )}

      <ConfirmDialog 
        open={deleteDialog.open} 
        title="حذف المتغير" 
        message={`هل أنت متأكد من حذف متغير "${deleteDialog.variant?.name}"؟`} 
        onConfirm={handleDelete} 
        onCancel={() => setDeleteDialog({ open: false, index: -1, variant: null })} 
        variant="danger" 
      />
    </div>
  )
}
